import React, { type FC, type JSX, useContext, useRef, useState } from 'react';
import dynamic from 'next/dynamic';
import { useTranslation } from '@/app/components/translations/Translation.tsx';
import { useCommonClient } from '@/app/common/commonClientProvider.tsx';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider.tsx';
import { getSearchModeFromSearchParams } from '@/app/components/search/utils/query2.ts';
import type { PROMOTION_TYPES } from '@/domainLogic/index.ts';
import useIsClient from '@/app/components/search/hooks/useIsClient.ts';
import usePriceMode from '@/app/components/search/hooks/usePriceMode.ts';
import PriceLoader from '@/app/components/search/components/EvolutionCard/Loaders/Price';
import type { HandleDealCardCtaCallBack } from '@/app/components/search/components/EvolutionCard/types.ts';
import { getCardCtaLinkProps } from '@/app/components/search/components/EvolutionCard/utils/getCardCtaLinkProps.ts';
import Spacer from '@/app/components/search/components/EvolutionCard/Spacer';
import { Icon } from '@/app/common/icons/Icon';
import type { DealCard } from '../../../../utils/buildDealCards/dealCardTypes';
import { CardsContext } from '../../../SearchPageContainer/ListContainer/CardsGrid/CardsContext';
import PriceDetails from './PriceDetails';
import PriceBottom from './PriceBottom';
import { CtaButtonCard, OverlayChevronIcon, PriceContainer, PriceCTARow, PriceInfoWrapper } from './styled';
import { decorateTaxes } from '../../utils/decorateTaxes';
import { decoratePriceAge } from '../../utils/getPriceDetails';
import { hasPackageSavingsEnabled } from '../../../../utils/productsResponse';
import BadgeSlot from '../../Badges/BadgeSlot.tsx';
import { BADGE_SLOTS } from '../../Badges/types';

type PriceProps = {
  dealCard: DealCard;
  isOverlay: boolean;
  promotionTypes: PROMOTION_TYPES[];
  promotionType: PROMOTION_TYPES | null;
  handleDealCardCtaCallBack: HandleDealCardCtaCallBack;
  deepLink?: string;
  isNotClickable?: boolean;
  isHO?: boolean;
};

const PackageSavingsModal = dynamic(() => import('../../../Modal/Modals/PackageSavingsModal'), { ssr: false });

const Price: FC<PriceProps> = ({
  dealCard,
  isOverlay,
  promotionTypes,
  promotionType,
  handleDealCardCtaCallBack,
  deepLink,
  isNotClickable,
  isHO,
}): JSX.Element => {
  const isClient = useIsClient();
  const { t } = useTranslation();
  const priceContainerRef = useRef<HTMLDivElement>(undefined);
  const { localization, searchParams } = usePageWrapperData();
  const { isMobile } = useCommonClient();
  const { isWaiting, isLoading, travellersCount } = useContext(CardsContext);
  const { lang } = localization;
  const [showPackageSavingsModal, setShowPackageSavingsModal] = useState(false);
  const { showTotalPrice, showDoublePrice } = usePriceMode();
  const searchMode = getSearchModeFromSearchParams(searchParams);
  const priceFoundShort = t('vacation.oss.cards.price_found_short');
  const priceFound = t('vacation.oss.cards.price_found');
  const isOverlayAndMobile = isOverlay && isMobile;
  const { age, taxesTotal, taxes, isPerPerson } = dealCard?.product?.rate?.price || {};
  const showPrice = !isWaiting && !isLoading && dealCard?.product?.accommodation;
  const priceContainerClassName = '';
  const ageText = decoratePriceAge(isOverlay ? priceFoundShort : priceFound, lang, age);
  const packageSavingsEnabled = hasPackageSavingsEnabled(dealCard, localization);
  const hasPromotions = promotionTypes && promotionTypes.length > 0;
  const showBadgesOnTop = !isOverlay && hasPromotions;
  const showBadgesNearPrice = !isMobile && !isOverlay && hasPromotions;
  const soldOut = dealCard?.product?.dates?.soldOut;
  const showOuterSpacer = !soldOut && !(isOverlay && isMobile);
  // Double pricing shows the group total next to the per-person price, so the local fees follow the
  // total: per person × travellers for per-person rates, the server stay total otherwise. Same basis
  // the taxes popover uses in this mode, so trigger and breakdown agree.
  const doublePriceTaxes = isPerPerson ? (taxes ?? 0) * travellersCount : taxesTotal;
  const taxesText = decorateTaxes({
    taxes: showDoublePrice ? doublePriceTaxes : showTotalPrice ? taxesTotal : taxes,
    localization,
    searchMode,
    t,
  });

  if (!showPrice) {
    return (
      <PriceContainer isOverlay={isOverlay} isWaiting={isWaiting}>
        <PriceLoader isOverlay={isOverlay} isMobile={isMobile} />
      </PriceContainer>
    );
  }

  return (
    <>
      {showOuterSpacer && <Spacer hideOnMobile={!isOverlay} minHeight={isOverlay ? '12px' : '24px'} />}
      <PriceContainer
        ref={priceContainerRef}
        isOverlay={isOverlay}
        isWaiting={isWaiting}
        isOverlayAndMobile={isOverlayAndMobile}
        className={priceContainerClassName}
        hasAgeText={!!ageText}
        hasTaxesText={!!taxesText}
      >
        {showBadgesOnTop && (
          <BadgeSlot
            name={isMobile ? [BADGE_SLOTS.DEAL_HIGHLIGHTS, BADGE_SLOTS.PRICE_ADDONS] : BADGE_SLOTS.DEAL_HIGHLIGHTS}
            promotionTypes={promotionTypes}
            dealCard={dealCard}
            isOverlay={isOverlay}
          />
        )}
        <Spacer hideOnMobile={!isOverlay} minHeight={isOverlay ? '0' : '24px'} />
        <PriceCTARow isOverlay={isOverlay}>
          <PriceInfoWrapper isOverlay={isOverlay}>
            {showBadgesNearPrice && (
              <BadgeSlot
                name={BADGE_SLOTS.PRICE_ADDONS}
                promotionTypes={promotionTypes}
                dealCard={dealCard}
                isOverlay={isOverlay}
              />
            )}
            <PriceDetails
              isOverlay={isOverlay}
              dealCard={dealCard}
              promotionType={promotionType}
              showTotalPrice={showTotalPrice}
              showDoublePrice={showDoublePrice}
            />
            <PriceBottom
              isOverlay={isOverlay}
              ageText={ageText}
              taxesText={taxesText}
              dealCard={dealCard}
              showTotalPrice={showTotalPrice}
              showDoublePrice={showDoublePrice}
            />
          </PriceInfoWrapper>
          {!isOverlay && isNotClickable && (
            <CtaButtonCard>{isHO ? t('vacation.oss.card.cta.ho') : t('vacation.oss.curated.card.cta')}</CtaButtonCard>
          )}
          {!isOverlay && !isNotClickable && (
            <CtaButtonCard {...getCardCtaLinkProps({ deepLink, handleDealCardCtaCallBack })}>
              {isHO ? t('vacation.oss.card.cta.ho') : t('vacation.oss.curated.card.cta')}
            </CtaButtonCard>
          )}
          {isOverlay && (
            <OverlayChevronIcon>
              <Icon category="Arrows" name="icon-chevron-right" size={20} footprint={16} color="iconPrimary" alt="" />
            </OverlayChevronIcon>
          )}
        </PriceCTARow>
        {isClient && packageSavingsEnabled && (
          <PackageSavingsModal
            isOpen={showPackageSavingsModal}
            onClose={() => setShowPackageSavingsModal(false)}
            dealCard={dealCard}
          />
        )}
      </PriceContainer>
    </>
  );
};

export default Price;
